import { useState, useEffect } from 'react';
import { Globe, ExternalLink } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { Card } from '../common/Card';
import { supabase } from '../../lib/supabase';

interface Props {
  businessId: string;
  tier: string;
}

const INPUT: React.CSSProperties = {
  width: '100%', padding: '8px 12px', borderRadius: 8,
  border: `1px solid ${palette.g200}`, fontSize: 13.5, color: palette.g800,
  fontFamily: 'inherit', outline: 'none', resize: 'vertical',
};

export function DirectoryListingCard({ businessId, tier }: Props) {
  const [listed, setListed] = useState(false);
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const isBusiness = tier === 'business' || tier === 'trial';

  useEffect(() => {
    supabase.from('businesses')
      .select('directory_listed, directory_description')
      .eq('id', businessId)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setListed(data.directory_listed ?? false);
          setDescription(data.directory_description ?? '');
        }
        setLoaded(true);
      });
  }, [businessId]);

  const handleToggle = async () => {
    const next = !listed;
    setSaving(true);
    await supabase.from('businesses')
      .update({ directory_listed: next })
      .eq('id', businessId);
    setListed(next);
    setSaving(false);
  };

  const handleSaveDesc = async () => {
    setSaving(true);
    await supabase.from('businesses')
      .update({ directory_description: description.trim().slice(0, 160) || null })
      .eq('id', businessId);
    setSaving(false);
  };

  if (!loaded) return null;

  return (
    <Card title="Directory Listing">
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Globe size={18} color={listed ? palette.green : palette.g400} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: palette.g800 }}>
            {listed ? 'Your business is listed in the directory' : 'Not listed in the directory'}
          </div>
          <div style={{ fontSize: 12, color: palette.g500, marginTop: 2 }}>
            {listed
              ? 'Clients can find you at crewbit.ca/explore'
              : 'Enable to let clients find your business on crewbit.ca/explore'}
          </div>
        </div>
        {isBusiness ? (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={listed}
              onChange={handleToggle}
              disabled={saving}
              style={{ width: 18, height: 18, cursor: 'pointer' }}
            />
          </label>
        ) : (
          <span style={{ fontSize: 11, fontWeight: 600, color: palette.amber, background: palette.amberWash, padding: '3px 10px', borderRadius: 6 }}>
            Business tier
          </span>
        )}
      </div>

      {listed && isBusiness && (
        <>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: palette.g500, display: 'block', marginBottom: 4 }}>
              Description ({description.length}/160)
            </label>
            <textarea
              style={{ ...INPUT, minHeight: 60 }}
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 160))}
              placeholder="A short description for the directory listing..."
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <a
              href="https://crewbit.ca/explore"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 12, color: palette.accentDeep, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}
            >
              View directory <ExternalLink size={10} />
            </a>
            <button
              onClick={handleSaveDesc}
              disabled={saving}
              style={{
                padding: '6px 16px', borderRadius: 6, border: 'none',
                background: saving ? palette.g300 : palette.accent, color: palette.white,
                fontSize: 12, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </>
      )}

      {!isBusiness && (
        <div style={{ fontSize: 12, color: palette.g500, background: palette.g50, padding: 12, borderRadius: 8, marginTop: 4 }}>
          Directory listing is available on the Business tier. Upgrade to list your business publicly.
        </div>
      )}
    </Card>
  );
}
